<template>
<v-dialog :value="value" width="800" persistent>
  <v-card color="fill">
    <v-card-title :class="title">
      <span>Mass Copy Ad Features</span>
      <v-col cols="auto" class="ml-auto my-auto pa-0">
        <span class="text-body-1">
          Selected Features: ({{ featureIds.length }})
        </span>
      </v-col>
    </v-card-title>
    <v-container class="ma-0 pa-0 pt-4" fluid>
        <v-data-table
          v-model="selectedAds"
          :headers="headers"
          :items="uniqueAds"
          :loading="loading"
          dense
          height="384px"
          show-select
          fixed-header
          :footer-props="{'items-per-page-options': [-1]}"
          :items-per-page="-1">
          <template v-slot:[`item.start_date`]="{ item }">
            <span v-if="item.start_date">
              {{ $dateConfig.formatDate(item.start_date) }}</span>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.end_date`]="{ item }">
            <span v-if="item.end_date">
              {{ $dateConfig.formatDate(item.end_date) }}</span>
            <span v-else>-</span>
          </template>
        </v-data-table>
        <v-divider />
    </v-container>
    <v-toolbar flat color="fill">
      <v-col cols="auto">
        <span v-if="noValidAds" class="warning--text">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          No valid feature destinations found
        </span>
        <span v-else>
          Selected Ads: ({{ selectedAds.length }})
        </span>
      </v-col>
      <v-spacer />
      <v-btn text :class="body" @click="$emit('update:value', false)">
        Cancel
      </v-btn>
      <v-btn 
        :disabled="copyDisabled" 
        :loading="copyInProgress"
        color="warning"
        :class="body" 
        class="white--text ml-2" 
        @click.stop="openConfirmDialog">
        Copy
      </v-btn>
    </v-toolbar>
  </v-card>
  <FeaturePromoWarnings
    v-if="warningsModal" 
    v-model="warningsModal" 
    multiple
    :selectedAd="selectedAd" 
    :gappedPromos="gappedPromos"
    :move="false"
    @cancel="closeWarningsModal" 
    @confirmAction="confirmAction">
  </FeaturePromoWarnings>
  <MassCopyConfirm
    v-if="confirmDialog" 
    v-model="confirmDialog"
    @cancel="closeConfirmDialog" 
    @confirmAction="confirmAction">
  </MassCopyConfirm>
</v-dialog>
</template>
<script>
import { utils } from '@/mixins/utils'
import { displayHelpers } from '@/mixins/display-helpers'
import { notification } from '@/mixins/notification'
import { format } from '@/mixins/format'
import { displayAlert } from '@/mixins/alert'
const MassCopyConfirm = () => import('@/components/features/MassCopyConfirm.vue')
import Ads from '@/axios/ad-endpoints'
import Features from '@/axios/feature-endpoints'
import FeaturePromoWarnings from './promotions/FeaturePromoWarnings.vue'
export default {
  name: 'FeatureMassCopy',
  mixins: [displayHelpers, format, notification, displayAlert, utils],
  components: { MassCopyConfirm, FeaturePromoWarnings },
  data() {
    return {
      confirmDialog: false,
      copyDestinations: [],
      selectedAds: [],
      warningsModal: false,
      gappedPromos: [],
      loadingAds: false,
      copyInProgress: false,
      valid: true,
      noValidAds: false,
      headerProps: {
        class: 'accent white--text'
      },
      baseHeaders: [
        { value: 'data-table-select', class: 'accent', sortable: false },
        { text: 'Destination Ad Name', value: 'ad_name' },
        { text: 'Ad Group', value: 'ad_group_name' },
        { text: 'Eligible Features', value: 'feature_count' },
        { text: 'Ad Start', value: 'start_date' },
        { text: 'Ad End', value: 'end_date' }
      ]
    }
  },
  props: {
    value: Boolean,
    selectedAd: Object, 
    selectedFeatures: Array,
    pages: Array,
    currentPage: Object,
    adParty: Object
  },
  async created() {
    await this.getMassCopyDestinations()
  },
  computed: {
    headers() {
      return this.baseHeaders.map(header => {
        return { ...this.headerProps, ...header }
      })
    },
    copyDisabled () {
      return this.loading || (this.selectedAds.length === 0)
    },
    loading () {
      return this.loadingAds || this.copyInProgress
    },
    featureIds () {
      if (this.selectedFeatures.length > 0) {
        return this.selectedFeatures.map(feature => feature.id)
      }
      return []
    },
    nonPromoOnly () {
      if (this.selectedFeatures.length > 0) {
        return this.selectedFeatures.every(feature => feature.promo_item_group_ids.length === 0)
      }
      return false
    },
    uniqueAds () {
      if (this.copyDestinations.length > 0) {
        // copyDestinations includes feature-level info, so we need to manipulate to pull distinct list of ads
        const grouped = Object.groupBy(this.copyDestinations, (dest) => dest.id) // ad id
        return Object.keys(grouped).map(adId => {
          const adFeatures = grouped[adId]
          const adInfo = adFeatures[0]
          return { 
            ...adInfo,
            eligible_features: adFeatures,
            feature_count: adFeatures.length
          }
        })
      }
      return []
    }
  },
  methods: {
    async getMassCopyDestinations () {
      this.loadingAds = true
      try {
        const { data } = await Ads.getValidMassCopyDestinations(this.selectedAd.id, this.featureIds, this.nonPromoOnly)
        this.copyDestinations = data
        if (this.copyDestinations.length === 0) {
          this.noValidAds = true
        }
      } catch (e) {
        console.error(e)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to retrieve ad list: ${e}` })
      } finally {
        this.loadingAds = false
      }
    },
    async massCopy () {
      this.copyInProgress = true
      try {
        const promises = this.selectedAds.flatMap(ad => this.copyFeatures(ad))
        await Promise.all(promises)
        this.$store.dispatch('setSnackbar', { status: 'success', text: 'Features copied successfully' })
        this.$emit('closeMassCopy')
      } catch (err) {
        console.error(err)
        this.handleError(err)
      } finally {
        this.copyInProgress = false
      }
    },
    async copyFeatures (ad) {
      // TO DO (later): add dedicated mass copy API endpoint so we don't need to make a billion API calls
      // must be executed sequentially to prevent potential page 7 feature location dupes
      for (const feature of ad.eligible_features) {
        const payload = await this.getCopyPayload(ad, feature)
        await Features.copyAdFeature(payload)
      }
    },
    async getCopyPayload(destAd, feature) {
      const selectedFeature = this.selectedFeatures.find(f => f.id === feature.source_feature_id)

      let target_feature_id = feature.target_feature_id
      const { data } = await Features.getAdFeatureById(target_feature_id)

      if (!this.isEmptyFeature(data)) {
        const targetFeature = data
        const emptyFeature = await this.getAvailableFeatureLocation(destAd)
        if (!emptyFeature) {
          throw new Error('Feature could not be copied due to a lack of available positions on Page 7')
        }
        // prep to move the feature currently occupying the target location to Page 7
        targetFeature.position = JSON.parse(JSON.stringify(emptyFeature.position))
        targetFeature.page = JSON.parse(JSON.stringify(emptyFeature.page))
         // prep to move the existing empty feature from Page 7 to the target location
        emptyFeature.position = JSON.parse(JSON.stringify(selectedFeature.position))
        emptyFeature.page = JSON.parse(JSON.stringify(selectedFeature.page))
        // update feature locations accordingly
        await Features.updateAdFeature(targetFeature.id, targetFeature)
        await Features.updateAdFeature(emptyFeature.id, emptyFeature)
        // adjust the target feature reference to the empty feature, now in our desired location
        target_feature_id = emptyFeature.id
      }
      return {
        move: false,
        item_source: destAd.item_source,
        ad_id: this.selectedAd.id,
        ad_group_id: this.adParty.id,
        start_date: destAd.start_date,
        end_date: destAd.end_date,
        feature_id: feature.source_feature_id,
        dest_feature_id: target_feature_id,
        dest_ad_id: destAd.id,
        dest_pig_ids: feature.dest_pig_ids || [],
        dest_promo_ids: feature.dest_promo_ids || []
      }
    },
    async getAvailableFeatureLocation(ad) {
      const page = this.pages.find(p => p?.constant === 'PAGE_7')
      try {
        const positionRes = await Features.getEmptyPagePositions(ad.id, page.id)
        if (positionRes?.data?.length > 0) {
          const nextFeature = positionRes.data.toSorted((a, b) => a.display_order - b.display_order)[0]
          if (nextFeature?.feature_id) {
            const { data } = await Features.getAdFeatureById(nextFeature.feature_id)
            if (data?.id) return data
          }
        }
        return null
      } catch (err) {
        this.handleError(err)
      }
    },
    isEmptyFeature(feature) {
      return (feature?.promo_item_group_ids?.length === 0)
        && (feature?.non_promo_items?.length === 0)
         && (feature?.feature_text?.trim()?.length === 0)
    },
    confirmAction () {
      this.closeConfirmDialog()
      // this.closeWarningsModal()
      this.massCopy()
    },
    async openConfirmDialog () {
      await this.checkPromoGap()
      if (this.gappedPromos.length > 0) {
        return this.warningsModal = true
      }
      this.confirmDialog = true
    },
    closeConfirmDialog () {
      this.confirmDialog = false
    },
    closeWarningsModal () {
      this.warningsModal = false
    },
    async checkPromoGap () {
      try {
        this.gappedPromos = []
        for (const ad of this.selectedAds) {
          for (const feature of this.selectedFeatures) {
              const params = {
                source_ad_id: this.selectedAd.id,
                target_ad_id: ad.id, 
                source_feature_id: feature.id, 
                item_source: this.adParty.distribution_center
              }
              const { data } = await Features.checkPromoGap(params)
              data.forEach(promo => promo.feature_pos = feature.position.name)
              this.gappedPromos = [...this.gappedPromos, ...data]
            }
          }
        } catch (err) {
        this.handleError(err)
      }
    }
  }
}
</script>